export type FoundObjectLinkedConversation = {
  unread_count?: number | null;
  announcement?: {
    object_attributes?: Record<string, unknown> | null;
    reference_code?: string | null;
    status?: string | null;
    messaging_allowed?: boolean | null;
    found_object_status?: string | null;
  } | null;
};

/** Statuts d’objet pour lesquels l’inbox messagerie prestataire masque la conversation. */
export const PROVIDER_INBOX_HIDDEN_FOUND_OBJECT_STATUSES = [
  'to_ship',
  'in_transit',
  'claimed',
  'disposed',
] as const;

/** Conversations consultables via le filtre « Restitués » (expédition + restitution). */
export const PROVIDER_RESTORED_INBOX_FOUND_OBJECT_STATUSES = [
  'to_ship',
  'in_transit',
  'claimed',
] as const;

/** Objet trouvé lié à une conversation (attributs ou référence FO-CONTACT-{id}). */
export function getFoundObjectIdFromConversation(
  conversation: FoundObjectLinkedConversation
): number | null {
  const attrs = conversation?.announcement?.object_attributes;
  const fromAttrs = Number(attrs?.found_object_id);
  if (Number.isFinite(fromAttrs) && fromAttrs > 0) return fromAttrs;

  const ref = String(conversation?.announcement?.reference_code ?? '');
  const match = ref.match(/^FO-CONTACT-(\d+)$/);
  if (match?.[1]) {
    const parsed = Number.parseInt(match[1], 10);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }

  return null;
}

/** Conversation hors inbox Messages (à expédier / transit / restitué / éliminé, ou annonce clôturée).
 * Note : l’API liste encore to_ship / in_transit (impression du bon) ; l’inbox les filtre ici. */
export function isConversationHiddenBecauseRestituted(
  conversation: FoundObjectLinkedConversation
): boolean {
  const foStatus = conversation.announcement?.found_object_status;
  if (
    foStatus != null &&
    (PROVIDER_INBOX_HIDDEN_FOUND_OBJECT_STATUSES as readonly string[]).includes(foStatus)
  ) {
    return true;
  }

  const foId = getFoundObjectIdFromConversation(conversation);
  if (!foId) return false;

  if (conversation.announcement?.messaging_allowed === false) return true;
  const status = conversation.announcement?.status;
  return status != null && status !== 'active';
}

/** Conversation du filtre « Restitués » (à expédier, transit, restitué). */
export function isConversationInRestoredInbox(
  conversation: FoundObjectLinkedConversation
): boolean {
  const foStatus = conversation.announcement?.found_object_status;
  return (
    foStatus != null &&
    (PROVIDER_RESTORED_INBOX_FOUND_OBJECT_STATUSES as readonly string[]).includes(foStatus)
  );
}

/** Nombre de messages non lus par objet trouvé (tous statuts inclus). */
export function buildFoundObjectUnreadCountMap(
  conversations: FoundObjectLinkedConversation[]
): Map<number, number> {
  const map = new Map<number, number>();
  for (const conversation of conversations) {
    const foundObjectId = getFoundObjectIdFromConversation(conversation);
    if (!foundObjectId) continue;
    const unread = conversation.unread_count ?? 0;
    if (unread <= 0) continue;
    map.set(foundObjectId, (map.get(foundObjectId) ?? 0) + unread);
  }
  return map;
}

export function getFoundObjectUnreadLabel(count: number): string {
  if (count <= 0) return '';
  if (count === 1) return 'Nouveau message';
  return `${count} non lus`;
}

type ConversationWithPossibleLabel = FoundObjectLinkedConversation & {
  id?: number;
  last_message?: { content?: string | null } | null;
};

/** Conversations liées à un objet trouvé (messagerie restitution). */
export function getConversationsForFoundObject(
  conversations: ConversationWithPossibleLabel[],
  foundObjectId: number
): ConversationWithPossibleLabel[] {
  return conversations.filter((c) => getFoundObjectIdFromConversation(c) === foundObjectId);
}
